// SPDX-License-Identifier: AGPL-3.0-or-later

import * as GuildCommands from '@app/features/guild/commands/GuildCommands';
import styles from '@app/features/guild/components/modals/guild_tabs/CloneAllowedToggle.module.css';
import Guilds from '@app/features/guild/state/Guilds';
import Permission from '@app/features/permissions/state/Permission';
import {Logger} from '@app/features/platform/utils/AppLogger';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {Switch} from '@app/features/ui/components/form/Switch';
import {Permissions} from '@app/vendor/constants/ChannelConstants';
import {GuildFeatures} from '@app/vendor/constants/GuildConstants';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {useState} from 'react';

const logger = new Logger('CloneAllowedToggle');
interface CloneAllowedToggleProps {
	guildId: string;
	kind: 'emoji' | 'sticker';
}
export const CloneAllowedToggle = observer(({guildId, kind}: CloneAllowedToggleProps) => {
	const {t} = useLingui();
	const guild = Guilds.getGuild(guildId);
	const canManageGuild = Permission.can(Permissions.MANAGE_GUILD, {guildId});
	const [saving, setSaving] = useState(false);
	if (!guild) return null;
	const allowed = kind === 'emoji' ? guild.cloneEmojiAllowed : guild.cloneStickerAllowed;
	const handleChange = async (nextAllowed: boolean) => {
		if (saving || !canManageGuild) return;
		setSaving(true);
		try {
			const feature = kind === 'emoji' ? GuildFeatures.CLONE_EMOJI_DISABLED : GuildFeatures.CLONE_STICKER_DISABLED;
			await GuildCommands.toggleFeature(guildId, feature, !nextAllowed);
		} catch (error) {
			logger.error(`Failed to toggle clone-${kind}-allowed for guild ${guildId}`, error);
			ToastCommands.error(
				kind === 'emoji'
					? t`Failed to update emoji cloning permission. Please try again.`
					: t`Failed to update sticker cloning permission. Please try again.`,
			);
		} finally {
			setSaving(false);
		}
	};
	const label =
		kind === 'emoji' ? (
			<Trans>Allow others to clone your emojis</Trans>
		) : (
			<Trans>Allow others to clone your stickers</Trans>
		);
	const description =
		kind === 'emoji' ? (
			<Trans>
				When enabled, members of other communities can use the in-app one-click "Clone" shortcut on your custom emojis.
				This does not prevent them from saving the image and uploading it themselves.
			</Trans>
		) : (
			<Trans>
				When enabled, members of other communities can use the in-app one-click "Clone" shortcut on your custom
				stickers. This does not prevent them from saving the image and uploading it themselves.
			</Trans>
		);
	return (
		<div className={styles.container}>
			<Switch
				value={allowed}
				onChange={handleChange}
				disabled={!canManageGuild || saving}
				label={label}
				description={description}
			/>
			{!canManageGuild && (
				<p className={styles.permissionHint}>
					<Trans>Only members with the Manage Community permission can change this.</Trans>
				</p>
			)}
		</div>
	);
});
